<template>
  <el-config-provider :locale="zhCn">
    <!-- 如果路由标记为hideLayout，则只显示router-view -->
    <div v-if="$route.meta.hideLayout" class="standalone-container">
      <router-view />
    </div>

    <!-- 否则显示完整的应用布局 -->
    <div v-else class="app-container">
      <!-- Vanta.js 全局背景容器 -->
      <div ref="vantaBackground" class="vanta-background"></div>
      
      <app-header></app-header>
      <main class="main-content">
        <router-view />
      </main>
      <app-footer />

      <!-- 全局登录表单 -->
      <div v-if="showLoginForm" class="global-login-overlay" @click.self="closeLoginForm">
        <div class="global-login-container">
          <login-form @login-success="handleLoginSuccess" @close="closeLoginForm"></login-form>
        </div>
      </div>
    </div>
  </el-config-provider>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted } from 'vue'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import AppFooter from '@/components/AppFooter.vue'
import { useUserStore } from '@/stores/userStore'
import LoginForm from '@/components/LoginForm.vue'
import AppHeader from './components/AppHeader.vue'

export default defineComponent({
  name: 'App',
  components: {
    AppFooter,
    LoginForm,
    AppHeader
  },
  setup() {
    const userStore = useUserStore()
    const loginFormRef = ref(null)
    const showLoginForm = ref(false)
    
    // Vanta.js 全局背景
    const vantaBackground = ref(null)
    let vantaEffect = null

    // 监听显示登录对话框事件
    const handleShowLoginDialog = () => {
      loginFormRef.value?.showDialog()
    }

    const handleShowLoginForm = () => {
      showLoginForm.value = true
    }

    const closeLoginForm = () => {
      showLoginForm.value = false
    }

    const handleLoginSuccess = (username) => {
      // 触发全局登录成功事件
      window.dispatchEvent(new CustomEvent('login-success', {
        detail: { username }
      }))
      closeLoginForm()
    }

    onMounted(() => {
      // 检查用户是否已登录
      userStore.checkAuth()

      // 添加全局登录事件监听
      document.addEventListener('show-login-dialog', handleShowLoginDialog)
      window.addEventListener('show-login-form', handleShowLoginForm)
      
      // 初始化 Vanta.js 全局背景
      if (window.VANTA && vantaBackground.value) {
        vantaEffect = window.VANTA.FOG({
          el: vantaBackground.value,
          // 减少交互控制，提升性能
          mouseControls: false,
          touchControls: false,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          highlightColor: 0x1a6e8c,
          midtoneColor: 0x6c93c8,
          lowlightColor: 0x1776e8,
          baseColor: 0x0,
          blurFactor: 0.55,
          speed: 0.30,
          zoom: 0.50
        })
      }
    })

    onUnmounted(() => {
      window.removeEventListener('show-login-form', handleShowLoginForm)
      
      // 清理 Vanta 效果
      if (vantaEffect) {
        vantaEffect.destroy()
      }
    })

    return {
      zhCn,
      loginFormRef,
      showLoginForm,
      closeLoginForm,
      handleLoginSuccess,
      vantaBackground
    }
  }
})
</script>

<style>
/* 全局样式 */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: #0a1628;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

html.dark,
body.dark,
.dark {
  background-color: #0f172a;
  color-scheme: dark;
  color: #e2e8f0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

:root {
  --primary-color: #3b82f6;
  --primary-light: #60a5fa;
  --primary-dark: #2563eb;
  --background-dark: #0f172a;
  --background-light: rgba(255, 255, 255, 0.07);
  --text-primary: rgba(255, 255, 255, 0.9);
  --text-secondary: rgba(255, 255, 255, 0.6);
  --danger-color: #ff4757;
  --success-color: #10b981;
  --warning-color: #ff9f43;
  --info-color: rgba(255, 255, 255, 0.5);
  --header-height: 44px;
  --footer-height: auto;

  --el-color-primary: #3b82f6;
  --el-color-primary-light-3: #60a5fa;
  --el-color-primary-light-5: #93c5fd;
  --el-color-primary-light-7: #bae6fd;
  --el-color-primary-light-9: #ecf5ff;
  --el-color-primary-dark-2: #2563eb;

  --el-color-success: #10b981;
  --el-color-warning: #ff9f43;
  --el-color-danger: #ff4757;
  --el-color-info: rgba(255, 255, 255, 0.5);
}

body,
html {
  background: #0a1628 !important;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  overflow: hidden;
}

.standalone-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  /* 使用深色背景 */
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  position: relative;
  z-index: 0;
  overflow: hidden;
  background: #0f172a;
  /* 纯色背景，不使用渐变 */
}

/* Vanta.js 全局背景容器 */
.vanta-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
  background: #0a1628; /* 设置初始背景色，避免白色闪现 */
  /* 启用GPU硬件加速 */
  transform: translateZ(0);
  will-change: transform;
}

/* 完全移除背景装饰元素 */
.app-container::before,
.app-container::after {
  display: none !important;
  content: none !important;
  background: none !important;
  animation: none !important;
}

/* 移除动画 */
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
  margin-top: var(--header-height);
  padding-bottom: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - var(--header-height) - var(--footer-height));
  min-height: 0;
}

.app-footer {
  position: relative;
  width: 100%;
  z-index: 10;
  background: transparent;
}

/* 全局登录表单样式 */
.global-login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  backdrop-filter: blur(5px);
}

.global-login-container {
  width: auto;
  /* 改为自动宽度 */
  max-width: 95%;
  /* 增加最大宽度 */
  background: transparent;
  border-radius: 16px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(59, 130, 246, 0.2);
  overflow: hidden;
  animation: fade-in 0.3s ease;
  position: relative;
  z-index: 3001;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 按钮样式覆盖 */
.el-button--primary {
  background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
  border: none;
  border-radius: 12px;
  height: 48px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0 24px;
  position: relative;
  overflow: hidden;
}

.el-button--primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0) 100%);
}

.el-button--primary:hover,
.el-button--primary:focus {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}

.el-button--primary:active {
  transform: translateY(1px);
  box-shadow: 0 2px 10px rgba(59, 130, 246, 0.2);
}

.el-button--default {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  border-radius: 12px;
  height: 48px;
  font-weight: 500;
}

.el-button--default:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* 消息提示样式 */
.el-message {
  background: rgba(20, 22, 30, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
  padding: 15px 20px !important;
}

.el-message--success {
  border-left: 4px solid var(--success-color) !important;
}

.el-message--error {
  border-left: 4px solid var(--danger-color) !important;
}

.el-message--warning {
  border-left: 4px solid var(--warning-color) !important;
}

.el-message__content {
  color: var(--text-primary) !important;
  font-size: 15px !important;
}

.el-message__icon {
  margin-right: 10px !important;
  font-size: 18px !important;
}

.el-message--success .el-message__icon {
  color: var(--success-color) !important;
}

.el-message--error .el-message__icon {
  color: var(--danger-color) !important;
}

.el-message--warning .el-message__icon {
  color: var(--warning-color) !important;
}

/* 标签样式覆盖 */
.el-tag--danger {
  background-color: rgba(255, 71, 87, 0.1) !important;
  border-color: rgba(255, 71, 87, 0.2) !important;
  color: #ff4757 !important;
}

.el-tag--warning {
  background-color: rgba(255, 159, 67, 0.1) !important;
  border-color: rgba(255, 159, 67, 0.2) !important;
  color: #ff9f43 !important;
}

.el-tag--success {
  background-color: rgba(59, 130, 246, 0.1) !important;
  border-color: rgba(59, 130, 246, 0.2) !important;
  color: #3b82f6 !important;
}

/* 上传组件样式覆盖 */
.el-upload-dragger {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 2px dashed rgba(255, 255, 255, 0.15) !important;
  border-radius: 12px !important;
}

.el-upload-dragger:hover {
  background: rgba(59, 130, 246, 0.08) !important;
  border-color: var(--primary-color) !important;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}

/* 添加玻璃拟态效果类 */
.glass-card {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  border: none;
}

/* 动画类 */
.animate-pulse {
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    opacity: 0.7;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.7;
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }

  100% {
    transform: translateY(0px);
  }
}

/* 全局覆盖 Element UI 的 Alert 样式 */
.el-alert--warning {
  background-color: rgba(59, 130, 246, 0.1) !important;
  border-color: rgba(59, 130, 246, 0.2) !important;
}

.el-alert--warning .el-alert__icon {
  color: #3b82f6 !important;
}

.el-alert--warning .el-alert__title {
  color: #3b82f6 !important;
}

.el-alert--warning .el-alert__content {
  color: #3b82f6 !important;
}

/* 添加全局样式来禁用可能的三点菜单 */
.el-menu-item.el-tooltip__trigger,
.el-menu--popup-container,
.el-menu--collapse-transition,
.el-menu--popup,
.el-icon-more,
.el-dropdown,
.el-dropdown-menu {
  display: none !important;
}

/* 确保登录按钮直接显示 */
.app-header .login-button {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* 确保所有内容都在背景之上 */
.app-header {
  position: relative;
  z-index: 20;
}

.global-login-overlay {
  z-index: 3000;
}

/* 确保登录容器能够适应子组件宽度 */
.global-login-container>* {
  width: 100%;
}

input,
select,
textarea {
  font-size: 16px !important;
  /* 防止 iOS 自动放大 */
  max-height: none !important;
}

@media screen and (-webkit-min-device-pixel-ratio: 0) {

  select,
  textarea,
  input {
    font-size: 16px !important;
  }
}
</style>
